import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { calculateStrategyAPY } from '@/domain/euler/shared/onchainHelpers'
import { useQuery } from '@tanstack/react-query'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { useMemo } from 'react'
import type { Address } from 'viem'
import { usePublicClient } from 'wagmi'

const CHUNK_SIZE = 50

interface UseBatchELendAPYParams {
  vaultAddresses: Address[]
  enabled?: boolean
}

/**
 * Map of lowercased vault address → live APY data. Always key lookups with
 * `address.toLowerCase()` so consumers match regardless of the casing they
 * happened to pass in; the stable query key shares one cache entry across
 * mixed-casing call sites so the stored casing is not under any single
 * caller's control.
 */
interface UseBatchELendAPYResult {
  apyMap: Map<string, { borrowAPY: number; supplyAPY: number; utilization: number }>
  isPending: boolean
}

interface VaultAPYData {
  interestRate: bigint
  totalBorrows: bigint
  totalAssets: bigint
  interestFee: bigint
}

const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60

export function useBatchELendAPY({ vaultAddresses, enabled = true }: UseBatchELendAPYParams): UseBatchELendAPYResult {
  const publicClient = usePublicClient({ chainId: E_LEND_CHAIN_ID })

  // Stable, order-independent cache key so multiple call sites building the
  // same vault-address list (possibly from different memoization paths) hit
  // the same React Query entry and dedupe the multicall.
  const stableKey = useMemo(
    () =>
      vaultAddresses
        .map((a) => a.toLowerCase())
        .sort()
        .join(','),
    [vaultAddresses],
  )

  const { data: vaultDataMap, isPending } = useQuery({
    queryKey: ['elend-live-apy', stableKey],
    queryFn: async () => {
      if (!publicClient || vaultAddresses.length === 0) {
        return new Map<string, VaultAPYData>()
      }

      const map = new Map<string, VaultAPYData>()

      for (let chunkStart = 0; chunkStart < vaultAddresses.length; chunkStart += CHUNK_SIZE) {
        const chunk = vaultAddresses.slice(chunkStart, chunkStart + CHUNK_SIZE)

        const contracts = chunk.flatMap((address) => [
          { address, abi: eVaultImplementationAbi, functionName: 'interestRate' as const },
          { address, abi: eVaultImplementationAbi, functionName: 'totalBorrows' as const },
          { address, abi: eVaultImplementationAbi, functionName: 'totalAssets' as const },
          { address, abi: eVaultImplementationAbi, functionName: 'interestFee' as const },
        ])

        const results = await publicClient.multicall({ contracts })

        for (let i = 0; i < chunk.length; i++) {
          const baseIdx = i * 4
          const address = chunk[i]
          if (!address) continue

          const r0 = results[baseIdx]
          const r1 = results[baseIdx + 1]
          const r2 = results[baseIdx + 2]
          const r3 = results[baseIdx + 3]

          const interestRate = r0?.status === 'success' ? (r0.result as bigint) : 0n
          const totalBorrows = r1?.status === 'success' ? (r1.result as bigint) : 0n
          const totalAssets = r2?.status === 'success' ? (r2.result as bigint) : 0n
          const interestFee = r3?.status === 'success' ? (r3.result as bigint) : 0n

          // Canonicalize on lowercase so consumers can look up regardless of
          // the casing whichever caller happened to populate the shared cache
          // entry first.
          map.set(address.toLowerCase(), { interestRate, totalBorrows, totalAssets, interestFee })
        }
      }

      return map
    },
    enabled: enabled && !!publicClient && vaultAddresses.length > 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000,
  })

  const apyMap = useMemo(() => {
    const map = new Map<string, { borrowAPY: number; supplyAPY: number; utilization: number }>()
    if (!vaultDataMap) return map

    for (const [address, data] of vaultDataMap.entries()) {
      const { interestRate, totalBorrows, totalAssets, interestFee } = data

      const ratePerSecond = Number(interestRate) / 1e27
      const borrowAPY = interestRate > 0n ? ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100 : 0
      const utilization = totalAssets > 0n ? Number(totalBorrows) / Number(totalAssets) : 0
      const supplyAPY = calculateStrategyAPY(interestRate, totalBorrows, totalAssets, interestFee)

      // `address` here is already lowercase — queryFn canonicalizes on write.
      map.set(address, { borrowAPY, supplyAPY, utilization })
    }

    return map
  }, [vaultDataMap])

  return { apyMap, isPending }
}
